import { ChangeEvent, useEffect, FocusEvent } from 'react';
import { FormControl, FormLabel, TextField } from '@mui/material';
import { SchoolSetting, YesNo } from '@youscience/commonapp-service-common';
import {
  CoreBox,
  CoreCheckbox,
  CoreFormControl,
  CoreFormControlLabel,
  CoreFormGroup,
  CoreTypography,
} from '@youscience/core';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import YesNoRadioGroupWithClearButton from '@components/YesNoRadioGroupWithClearButton';
import ContinueButton from '@components/ContinueButton';
import SectionAccordion from '@components/SectionAccordion';
import MonthDayYearDatePicker from '@components/MonthDayYearDatePicker';

import { REQUIRED_FIELD_MESSAGE } from '@constants/texts';

import FileUpload from '@pages/StudentDetailsPage/components/common/FileUpload';
import { getFilename } from '@pages/StudentDetailsPage/components/common/formUtils';

import { StyledFieldsContainer, StyledFieldWrapper } from '../../../Card.styles';

import { maxDate, minDate } from '../../../constants';

import { SchoolDetailsProps, SchoolProfileFields, CourseSettings, courseTypes } from '../../types';
import { validateFullDates } from '@utils/date/dateUtils';
import { FormState, FormStateErrors } from '@constants/commonApp/types';
import useFormSection from '@pages/RecommenderSpace/hooks/useFormSection';

const formatString = (value: unknown): string | undefined | null => {
  return value !== null && value !== undefined ? String(value) : value;
};

export const SchoolProfile = ({ onExpand, onSaveAndContinue, isExtended }: SchoolDetailsProps<SchoolProfileFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const {
    initialState: { schoolProfile: initialState },
    formState: { schoolProfile: formState },
    setSectionFormState,
  } = useRecommenderProfileStore((store) => store);

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
  const isFilledAllRequiredFields =
    formState.data.classSize &&
    formState.data.blockSchedule &&
    formState.data.graduation &&
    formState.data.counselorAcademicRatings &&
    formState.data.serviceHours.required &&
    (formState.data.serviceHours?.required === YesNo.No || formState.data.serviceHours?.description);

  const isGraduationDateErrorText = formState.errors.graduation
    ? formState.errors.graduation
    : isShowErrors && !formState?.data?.graduation
      ? REQUIRED_FIELD_MESSAGE
      : undefined;

  const initializeValues = () => {
    const schoolProfile = recommenderProfile?.schoolProfile?.schoolProfile;
    const percentages = schoolProfile?.percentages;
    const courses = schoolProfile?.courses;

    return {
      schoolProfile: schoolProfile?.schoolProfile,
      classSize: formatString(schoolProfile?.classSize ?? undefined) as string | undefined,
      expectedMidYear:
        schoolProfile?.expectedMidYear instanceof Date
          ? schoolProfile.expectedMidYear.toISOString()
          : schoolProfile?.expectedMidYear,
      percentages: {
        twoYear: formatString(percentages?.twoYear),
        fourYear: formatString(percentages?.fourYear),
        latino: formatString(percentages?.latino),
        americanIndian: formatString(percentages?.americanIndian),
        asian: formatString(percentages?.asian),
        africanAmerican: formatString(percentages?.africanAmerican),
        native: formatString(percentages?.native),
        white: formatString(percentages?.white),
        firstGeneration: formatString(percentages?.firstGeneration),
        usCitizen: formatString(percentages?.usCitizen),
        nonUsCitizen: formatString(percentages?.nonUsCitizen),
        freeLunch: formatString(percentages?.freeLunch),
        financialAid: formatString(percentages?.financialAid),
      },
      schoolSetting: schoolProfile?.schoolSetting,
      courses: {
        offered: courses?.offered ?? '',
        ap: {
          offering: formatString(courses?.ap?.offering),
          limit: formatString(courses?.ap?.limit),
        },
        ib: {
          offering: formatString(courses?.ib?.offering),
          limit: formatString(courses?.ib?.limit),
        },
        honors: {
          offering: formatString(courses?.honors?.offering),
          limit: formatString(courses?.honors?.limit),
        },
        cambridge: {
          offering: formatString(courses?.cambridge?.offering),
          limit: formatString(courses?.cambridge?.limit),
        },
      },
      blockSchedule: schoolProfile?.blockSchedule,
      graduation:
        schoolProfile?.graduation instanceof Date ? schoolProfile.graduation.toISOString() : schoolProfile?.graduation,
      internationalSchool: schoolProfile?.internationalSchool,
      counselorAcademicRatings: schoolProfile?.counselorAcademicRatings,
      serviceHours: {
        required: schoolProfile?.serviceHours?.required,
        description: schoolProfile?.serviceHours?.description,
      },
    };
  };

  const { handleFileChange } = useFormSection<SchoolProfileFields>({
    initialState,
    formState,
    initializeValues,
    sectionName: SectionName.SchoolProfile,
  });

  useEffect(() => {
    const isFormValid = Object.values(formState.errors).every((error) => error?.length === 0 || error === undefined);

    const newFormState = {
      ...formState,
      isFormValid,
    };

    setSectionFormState(SectionName.SchoolProfile, newFormState);
  }, [formState.errors]);

  const isObject = (obj: unknown): obj is Record<string, unknown> => {
    return obj !== null && typeof obj === 'object';
  };

  const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
    if (obj1 === obj2) return true;
    if (!isObject(obj1) || !isObject(obj2)) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  };

  const formStateHasChanged = (
    newState: FormState<SchoolProfileFields>,
    initialState: SchoolProfileFields,
  ): boolean => {
    return !deepEqual(newState.data, initialState);
  };

  const updateFormValues = (
    fieldUpdates: Partial<SchoolProfileFields>,
    errors?: FormStateErrors<SchoolProfileFields>,
  ) => {
    const newFormState = {
      ...formState,
      data: {
        ...formState.data,
        ...fieldUpdates,
      },
      errors: {
        ...errors,
      },
    };

    newFormState.errors = validateFullDates(newFormState.errors, [
      { label: 'expectedMidYear', value: newFormState.data.expectedMidYear },
      { label: 'graduation', value: newFormState.data.graduation },
    ]);

    if (!initialState) {
      newFormState.hasChanged = true;
    } else {
      newFormState.hasChanged = formStateHasChanged(newFormState, initialState);
    }

    setSectionFormState(SectionName.SchoolProfile, newFormState);
  };

  const handleDecimalFieldBlur = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;

    handleFieldChange(event.target.name, value || undefined);
  };

  const handleDecimalFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    const decimalRegex = /^\d{1,2}(\.\d{0,2})?$/;

    if ((!decimalRegex.test(value) && value !== '100') || value.length > 5) {
      value = value.slice(0, -1);
    }

    handleFieldChange(event.target.name, value);
  };

  const handleIntegerFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const integerRegex = /^[0-9]{1,3}$/;

    if (value === '') {
      handleFieldChange(event.target.name, undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } else if (isNaN(value as any)) {
      handleFieldChange(event.target.name, value.slice(0, -1));
    } else if (integerRegex.test(value)) {
      handleFieldChange(event.target.name, value);
    }
  };

  const handleFieldChange = (name: string, value: string | undefined) => {
    if (name.includes('courses.')) {
      const [type, property] = name.split('courses.')[1].split('.');

      const currentValue = (formState.data.courses?.[type] ?? {}) as CourseSettings;

      if (courseTypes.find((courseType) => courseType.key === type)) {
        updateFormValues({
          courses: {
            ...formState.data.courses,
            [type]: {
              ...currentValue,
              [property]: value,
            },
          },
        });
      }
    } else if (name.includes('percentages.')) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [percentages, property] = name.split('.');

      updateFormValues({
        percentages: {
          ...formState.data.percentages,
          [property]: value,
        },
      });
    } else if (name.includes('serviceHours.')) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [serviceHours, property] = name.split('serviceHours.');

      updateFormValues({
        serviceHours: {
          ...formState.data.serviceHours,
          [property]: value,
        },
      });
    } else {
      updateFormValues({ [name]: value });
    }
  };

  const handleDateChange = (fieldName: string) => (value: Date | null | string) => {
    const errors = handleDateError(fieldName, value);

    if (value && isValidDate(value) === '') {
      const date = new Date(value);
      updateFormValues(
        {
          [fieldName]: `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`,
        },
        errors,
      );
    } else {
      if (initialState && initialState[fieldName] === undefined) {
        updateFormValues({ [fieldName]: undefined }, errors);
      } else {
        updateFormValues({ [fieldName]: null }, errors);
      }
    }
  };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    if (field.includes('serviceHours.')) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [services, property] = field.split('.');

      updateFormValues({
        serviceHours: { ...formState.data.serviceHours, [property]: event.target.value },
      });
    } else {
      updateFormValues({ [field]: event.target.value });
    }
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({
      courses: {
        ...formState.data.courses,
        [event.target.name]: { offering: undefined, limit: undefined },
        offered: event.target.checked
          ? formState.data.courses?.offered?.split(',').concat(event.target.value).join(',')
          : formState.data.courses?.offered
              ?.split(',')
              .filter((course: string) => course !== event.target.value)
              .join(','),
      },
    });
  };

  const isValidDate = (date: Date | null | string) => {
    const isValid = date instanceof Date && !isNaN(date.getTime());

    if (!date) {
      return '';
    }

    if (!isValid) {
      return 'Please enter a valid date';
    }

    if (date && date < minDate) {
      return `Date must be after ${minDate.getFullYear() - 1}`;
    }

    if (date && date > maxDate) {
      return `Date must be before ${maxDate.getFullYear() + 1}`;
    }

    return '';
  };

  const handleDateError = (fieldName: string, value: Date | null | string) => {
    const errorText = isValidDate(value);

    const errors = { ...formState.errors };

    if (!errorText) {
      delete errors[fieldName];
    } else {
      errors[fieldName] = errorText;
    }

    return errors;
  };

  return (
    <SectionAccordion
      title={'School Profile'}
      onChange={onExpand}
      isExpanded={isExtended}
      isChanged={formState.hasChanged}
      isError={isShowErrors && !isFilledAllRequiredFields}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledFieldsContainer>
          <FileUpload
            title="Attach your school profile here."
            name="schoolProfile"
            initialFile={formState.data.schoolProfile}
            previewTitle="Change Explanation"
            filename={formState.data.schoolProfile ? getFilename('', 'schoolProfile') : undefined}
            onFileUpload={handleFileChange}
            onFileDelete={handleFileChange}
            error={formState.errors.schoolProfile}
          />

          <CoreTypography variant="subtitle2" component="h4">
            Graduating students
          </CoreTypography>

          <CoreBox
            sx={{
              display: 'grid',
              gap: '1.5rem',
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <StyledFieldWrapper>
              <FormControl fullWidth data-testid="expectedMidYear">
                <MonthDayYearDatePicker
                  name="expectedMidYear"
                  label="Approximately when are mid-year grades typically available?"
                  value={(formState?.data?.expectedMidYear as string) ?? undefined}
                  slotProps={{
                    inputProps: { clearable: true, onClear: () => handleDateChange('expectedMidYear') },
                    error: false,
                  }}
                  handleDateChange={handleDateChange('expectedMidYear')}
                  minDate={minDate}
                  maxDate={maxDate}
                  error={formState.errors.expectedMidYear}
                />
              </FormControl>
            </StyledFieldWrapper>

            <CoreFormControl fullWidth>
              <TextField
                name="classSize"
                label="Graduating class size"
                value={formState.data.classSize ?? ''}
                required
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
                error={isShowErrors && !formState.data.classSize}
                helperText={isShowErrors && !formState.data.classSize ? REQUIRED_FIELD_MESSAGE : undefined}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.twoYear"
                label="Percentage of graduating class attending 2-year college"
                value={formState.data.percentages?.twoYear ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.fourYear"
                label="Percentage of graduating class attending 4-year college"
                value={formState.data.percentages?.fourYear ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>
          </CoreBox>

          <CoreTypography variant="subtitle2" component="h4">
            Demographic information
          </CoreTypography>

          <CoreBox
            sx={{
              display: 'grid',
              justifyContent: 'space-between',
              gap: '1.5rem',
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <CoreFormControl fullWidth>
              <TextField
                name="percentages.latino"
                label="Percentage of students who are Hispanic or Latino/a/x"
                value={formState.data.percentages?.latino ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.americanIndian"
                label="Percentage of students who are American Indian or Alaska Native"
                value={formState.data.percentages?.americanIndian ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.asian"
                label="Percentage of students who are Asian"
                value={formState.data.percentages?.asian ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.africanAmerican"
                label="Percentage of students who are Black or African American"
                value={formState.data.percentages?.africanAmerican ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.native"
                label="Percentage of students who are Native Hawaiian or Other Pacific Islander"
                value={formState.data.percentages?.native ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.white"
                label="Percentage of students who are White"
                value={formState.data.percentages?.white ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>
          </CoreBox>

          <CoreTypography variant="subtitle2" component="h4">
            Other information
          </CoreTypography>

          <CoreBox
            sx={{
              display: 'grid',
              justifyContent: 'space-between',
              gap: '1.5rem',
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            <CoreFormControl fullWidth>
              <TextField
                name="percentages.firstGeneration"
                label="Percentage of students who are first-generation college"
                value={formState.data.percentages?.firstGeneration ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.usCitizen"
                label="Percentage of students who are US Citizens"
                value={formState.data.percentages?.usCitizen ?? ''}
                onChange={handleDecimalFieldChange ?? ''}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.nonUsCitizen"
                label="Percentage of students who are Non-US Citizens"
                value={formState.data.percentages?.nonUsCitizen ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.freeLunch"
                label="Percentage of students who receive free or reduced lunch"
                value={formState.data.percentages?.freeLunch ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>

            <CoreFormControl fullWidth>
              <TextField
                name="percentages.financialAid"
                label="Percentage of students receiving financial aid"
                value={formState.data.percentages?.financialAid ?? ''}
                onChange={handleDecimalFieldChange}
                onBlur={handleDecimalFieldBlur}
              />
            </CoreFormControl>
          </CoreBox>

          <RadioButtonGroupWithClearButton
            title="School Setting"
            name="schoolSetting"
            value={formState.data.schoolSetting}
            options={SchoolSetting}
            handleRadioChange={handleRadioChange}
            handleClear={() => updateFormValues({ schoolSetting: undefined })}
          />

          <StyledFieldWrapper>
            <CoreTypography variant="subtitle2" component="h4">
              Which of the following courses are offered at your school?
            </CoreTypography>

            <CoreFormControl>
              <FormLabel id="schoolSettingLabel">Select your options</FormLabel>

              <CoreFormGroup sx={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                {courseTypes.map((courseType) => (
                  <CoreBox
                    key={courseType.value}
                    sx={{
                      display: 'grid',
                      gap: '.5rem 1.5rem',
                      gridTemplateColumns: '1fr 1fr',
                    }}
                  >
                    <CoreFormControlLabel
                      label={<CoreTypography>{courseType.label}</CoreTypography>}
                      sx={{ gridColumn: '1 / span 2' }}
                      control={
                        <CoreCheckbox
                          key={`${courseType}Checkbox`}
                          name={courseType.key}
                          value={courseType.value}
                          onChange={handleCheckboxChange}
                          checked={formState.data.courses?.offered?.split(',').includes(courseType.value)}
                        />
                      }
                    />
                    {formState.data.courses?.offered?.split(',').includes(courseType.value) ? (
                      <>
                        <CoreFormControl fullWidth>
                          <TextField
                            key={`${courseType.value}offering`}
                            name={`courses.${courseType.key}.offering`}
                            label={`Number of ${courseType.label} courses offered`}
                            value={(formState.data.courses?.[courseType.key] as CourseSettings)?.offering ?? ''}
                            onChange={handleIntegerFieldChange}
                          />
                        </CoreFormControl>

                        <CoreFormControl fullWidth>
                          <TextField
                            key={`${courseType.value}limit`}
                            name={`courses.${courseType.key}.limit`}
                            label={`Maximum number of ${courseType.label} courses a student can take`}
                            value={(formState.data.courses?.[courseType.key] as CourseSettings)?.limit ?? ''}
                            onChange={handleIntegerFieldChange}
                          />
                        </CoreFormControl>
                      </>
                    ) : null}
                  </CoreBox>
                ))}
              </CoreFormGroup>
            </CoreFormControl>
          </StyledFieldWrapper>

          <YesNoRadioGroupWithClearButton
            title="Are classes taken on a block schedule?"
            name="blockSchedule"
            handleRadioChange={handleRadioChange}
            handleClear={() => updateFormValues({ blockSchedule: null })}
            value={formState.data.blockSchedule}
            required
            error={isShowErrors && !formState.data.blockSchedule}
            helperText={isShowErrors && !formState.data.blockSchedule ? REQUIRED_FIELD_MESSAGE : undefined}
          />

          <StyledFieldWrapper>
            <FormControl data-testid="graduation" sx={{ maxWidth: '400px' }}>
              <MonthDayYearDatePicker
                name="graduation"
                label="Graduation date"
                value={(formState?.data?.graduation as string) ?? undefined}
                required
                slotProps={{
                  inputProps: { clearable: true, onClear: () => handleDateChange('graduation') },
                  error: false,
                }}
                handleDateChange={handleDateChange('graduation')}
                minDate={minDate}
                maxDate={maxDate}
                error={isGraduationDateErrorText}
              />
            </FormControl>
          </StyledFieldWrapper>

          <YesNoRadioGroupWithClearButton
            title="Are you willing to provide applicants' academic ratings on the Common App Teacher evaluation form as a Counselor?"
            name="counselorAcademicRatings"
            value={formState.data.counselorAcademicRatings}
            handleRadioChange={handleRadioChange}
            handleClear={() => updateFormValues({ counselorAcademicRatings: null })}
            required
            error={isShowErrors && !formState.data.counselorAcademicRatings}
            helperText={isShowErrors && !formState.data.counselorAcademicRatings ? REQUIRED_FIELD_MESSAGE : undefined}
          />

          <StyledFieldWrapper>
            <YesNoRadioGroupWithClearButton
              title="Does your school require students to perform volunteer service?"
              name="serviceHours.required"
              testId="service-hours-radio-group"
              required
              value={formState.data.serviceHours?.required}
              handleRadioChange={handleRadioChange}
              handleClear={() => updateFormValues({ serviceHours: { required: null } })}
              error={isShowErrors && !formState.data.serviceHours?.required}
              helperText={isShowErrors && !formState.data.serviceHours?.required ? REQUIRED_FIELD_MESSAGE : undefined}
            />

            {formState.data.serviceHours?.required === '0' ? (
              <CoreFormControl fullWidth sx={{ marginTop: '1rem' }}>
                <TextField
                  name="serviceHours.description"
                  label="Please describe what is required"
                  required
                  value={formState.data.serviceHours?.description ?? ''}
                  onChange={(e) => handleFieldChange(e.target.name, e.target.value)}
                  inputProps={{ maxLength: 150 }}
                  error={isShowErrors && !formState.data.serviceHours?.description}
                  helperText={isShowErrors && !formState.data.serviceHours?.description ? REQUIRED_FIELD_MESSAGE : ''}
                />
              </CoreFormControl>
            ) : null}
          </StyledFieldWrapper>

          <ContinueButton onContinue={onSaveAndContinue} isDisabled={!formState.isFormValid} />
        </StyledFieldsContainer>
      )}
    </SectionAccordion>
  );
};
